import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { eligibleJobsService } from '../eligibleJobsService';
import { IEligibleJob, IGetEligibleJobsParams } from '../types';

type TUseInfiniteEligibleJobsParams = Omit<IGetEligibleJobsParams, 'page'> & {
  enabled?: boolean;
};

export function useInfiniteEligibleJobs(params: TUseInfiniteEligibleJobsParams) {
  const query = useInfiniteQuery({
    queryKey: ['eligible-jobs-infinite', params],
    queryFn: async ({ pageParam }) => {
      const result = await eligibleJobsService.getEligibleJobs({
        ...params,
        page: pageParam as number,
      });
      return result;
    },
    getNextPageParam: (lastPage, allPages) => {
      // If less than limit returned, no more pages
      const limit = params.limit || 15;
      return lastPage.jobs.length === limit ? allPages.length + 1 : undefined;
    },
    enabled: params.enabled ?? true,
    staleTime: 1000 * 30, // 30 seconds
    gcTime: 1000 * 60 * 2, // 2 minutes
    initialPageParam: 1,
  });

  const eligibleJobs = useMemo(
    () => query?.data?.pages.flatMap((page) => page.jobs) ?? [],
    [query.data?.pages],
  );

  const total = useMemo(() => query?.data?.pages[0]?.total ?? 0, [query.data?.pages]);

  return {
    eligibleJobs: eligibleJobs as IEligibleJob[],
    total,
    ...query,
  };
}
